import { FC } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import TableTab from "../../../components/tabs/table-tab";
import { FILTER_DATE, FILTER_ENTRY_TERMINAL, FILTER_PASS_TERMINAL, FILTER_FAIL_TERMINAL, FILTER_TIMEOUT_MINUTES, FILTER_RESULT } from "../../../constants/filter-types";
import { REPORTS_SEQUENTIAL_EVENT } from "../../../constants/menu-types";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import { DEF_PG_SIZE_REPORTS } from '../../../constants/maximum-page-size';
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import SequentialEventColumn from "../../../models/ColumnDefinitions/SequentialEventColumn";
import { makeFilterOnlyColumns, removeColumns } from "../../../models/utilities/columnHelper";
import { Filter } from "../../../models/Filter";
import { Lookup } from "../../../models/Lookup";
import LookupTypes from "../../../models/LookupTypes";
import SequentialEventSearch from "../../../models/search/SequentialEventSearch";
import { getReaderNamesWithPagination } from "../../../services/readerService";
import { auditRunReport } from "../../../services/auditService";
import { getUserFiltersByView } from "../../../services/userPreferenceService";
import { getSequentialEventReport } from "../../../services/reportService";
import { getErrorMessage } from "../../../services/utilities/ServiceError";
import ReaderSearch from "../../../models/search/ReaderSearch";
import { showError } from "../../../services/utilities/ServiceError";
import { getTodayDate } from "../../../utilities/DateUtils";
import { ListQuery } from "../../../models/ListQuery";
import { DEFAULT_TIMEOUT_SYSNAME, TIMEOUT_OPTIONS, TIMEOUT_LABELS, TIMEOUT_BY_SYSNAME, RESULT_OPTIONS } from "../../../constants/element-type";

interface SequentialEventReportProps {
    reportId: string;
}

const SequentialEventReport: FC<SequentialEventReportProps> = ({ reportId }) => {
    const { translate } = useLocale();
    const userLoginId = useSelector((state: any) => (state.userInfo.id));
    const currentMenuDetail = REPORTS_SEQUENTIAL_EVENT;

    const lookup: Lookup[] = [
        {
            name: "entryTerminalId",
            values: [],
            dataMethod: getReaderNamesWithPagination,
            searchAccessor: ReaderSearch.find(item => item.accessor === "name")?.searchOnName,
            isSingleSelect: true
        },
        {
            name: "passTerminalId",
            values: [],
            dataMethod: getReaderNamesWithPagination,
            searchAccessor: ReaderSearch.find(item => item.accessor === "name")?.searchOnName,
            isSingleSelect: true
        },
        {
            name: "failTerminalId",
            values: [],
            dataMethod: getReaderNamesWithPagination,
            searchAccessor: ReaderSearch.find(item => item.accessor === "name")?.searchOnName,
            isSingleSelect: true
        },
        {
            name: "timeoutMinutes",
            values: TIMEOUT_OPTIONS,
            isSingleSelect: true
        },
        {
            name: "result",
            values: RESULT_OPTIONS,
            isSingleSelect: true
        }
    ];

    const getOriginalStaticFilter = async (filterKey: string): Promise<Filter> => {
        switch (filterKey) {
            case "entryTerminalId":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    filterId: FILTER_ENTRY_TERMINAL,
                    label: translate.byKey(TranslationKeys.entryDevice),
                    options: [],
                    selected: [{
                        label: translate.byKey(TranslationKeys.required),
                        value: "require_filter",
                        ignore: true
                    }],
                };
            case "passTerminalId":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    filterId: FILTER_PASS_TERMINAL,
                    label: translate.byKey(TranslationKeys.passDevice),
                    options: [],
                    selected: [{
                        label: translate.byKey(TranslationKeys.required),
                        value: "require_filter",
                        ignore: true
                    }],
                };
            case "failTerminalId":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    filterId: FILTER_FAIL_TERMINAL,
                    label: translate.byKey(TranslationKeys.failDevice),
                    options: [],
                    selected: [{
                        label: translate.byKey(TranslationKeys.required),
                        value: "require_filter",
                        ignore: true
                    }],
                };
            case "timeoutMinutes":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    filterId: FILTER_TIMEOUT_MINUTES,
                    label: translate.byKey(TranslationKeys.timeoutMinutes),
                    options: [],
                    selected: [{
                        label: TIMEOUT_LABELS[DEFAULT_TIMEOUT_SYSNAME],
                        value: TIMEOUT_BY_SYSNAME[DEFAULT_TIMEOUT_SYSNAME],
                        elementSysname: DEFAULT_TIMEOUT_SYSNAME,
                        ignore: false
                    }],
                };
            case "dateOnly":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    filterId: FILTER_DATE,
                    label: translate.byKey(TranslationKeys.date),
                    options: [],
                    selected: [{
                        label: getTodayDate(),
                        value: getTodayDate(),
                        ignore: false
                    }],
                };
            default:
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: filterKey,
                    options: [],
                    selected: [{ ignore: true, label: translate.byKey(TranslationKeys.all), value: '*' }],
                };
        }
    };

    const initialiseUserPreferences = (setDetails: (userLoginId, currentMenuDetail, customFilter, reportId, staticFilterKeys) => void) => {
        const staticFilterKeys = ["dateOnly", "entryTerminalId", "passTerminalId", "failTerminalId", "timeoutMinutes"];
        let customFilter: Filter[] = [];

        getUserFiltersByView(userLoginId, currentMenuDetail, reportId).then(async (response) => {
            const dateFilter = response.find(filter => filter.filterId === FILTER_DATE);
            if (!!!dateFilter) {
                customFilter.push(await getOriginalStaticFilter("dateOnly"));
            }
            if (!response.find(filter => filter.filterId === FILTER_ENTRY_TERMINAL)) {
                customFilter.push(await getOriginalStaticFilter("entryTerminalId"));
            }
            if (!response.find(filter => filter.filterId === FILTER_PASS_TERMINAL)) {
                customFilter.push(await getOriginalStaticFilter("passTerminalId"));
            }
            if (!response.find(filter => filter.filterId === FILTER_FAIL_TERMINAL)) {
                customFilter.push(await getOriginalStaticFilter("failTerminalId"));
            }
            if (!response.find(filter => filter.filterId === FILTER_TIMEOUT_MINUTES)) {
                customFilter.push(await getOriginalStaticFilter("timeoutMinutes"));
            }
        }).catch((error) => {
            showError(error, toast.warn);
        }).finally(() => {
            setDetails(userLoginId, currentMenuDetail, customFilter, reportId, staticFilterKeys);
        });
    };

    const getDataMethod = async (listParameters?: ListQuery) => {
        try {
            await auditRunReport(reportId);
            return getSequentialEventReport(translate, toast.warn, listParameters);
        } catch (error) {
            return Promise.reject(getErrorMessage(error));
        }
    };

    return (
        <TableContextProvider
            maxPageSize={DEF_PG_SIZE_REPORTS}
            dataMethod={(listParameters) => getDataMethod(listParameters)}
            initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
            tableDescriptor={makeFilterOnlyColumns(SequentialEventColumn, ['tagCode', 'entryReader'])}
            searchDescriptor={SequentialEventSearch}
            useFilter
            useSort
            usePagination
            useSearch
            useTable
            lookup={lookup}
            loadDataOnDemand
            runDataOnDemandButtonLabel={translate.byKey(TranslationKeys.runReport)}
            getOriginalStaticFilter={getOriginalStaticFilter}
        >
            <TableTab
                title={''}
                reportTitle={translate.byKey(TranslationKeys.entryTestOutcomes)}
                selectRows={false}
                showFilterMenu={true}
                cyName="SequentialEventReport"
            />
        </TableContextProvider>
    );
};

export default SequentialEventReport;
